/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState } from "react";
import { TouchableOpacity, View } from "react-native";
import { Collapse, TableCell, TableRow, styled } from "@material-ui/core";

import { useSelector } from "react-redux";
import { RootState, SelfObject } from "store/types";

import { ProfileImage, Text } from "UpduoLibrary";
import {
  skalecalendarblue,
  upduoPrimaryBlack,
  upduoBlueBackground,
  upduoMediumDarkGray,
  upduoMediumGray,
} from "config/UpduoColors";
import { shortFormattedDateAndTimeString } from "config/Utilities";
import { info } from "react-icons-kit/feather/info";
import Icon from "react-icons-kit";

interface ActivityLogRowProps {
  session?: any;
  index?: number;
}

export default function ActivityLogRow({
  session,
  index,
}: ActivityLogRowProps) {
  const selfObject: SelfObject = useSelector(
    (state: RootState) => state.selfObject
  );

  const [expanded, setExpanded] = useState(false);

  const partner =
    session.teacher.id === selfObject.userId
      ? session.learner
      : session.teacher;
  const partnerName = `${partner.firstName} ${partner.lastName}`;

  const minutes = Math.floor(session.duration / 60);
  const seconds = session.duration % 60;

  const dateTime = shortFormattedDateAndTimeString(new Date(session.created));

  let cancelledReason = "";
  let cancelledExplanation = "";

  switch (session.cancelledReason) {
    case "TOO_SHORT":
      cancelledReason = "Too short";
      cancelledExplanation =
        "This session wasn’t counted as a complete session because it didn’t meet the minimum time requirement. Try to talk more with your partner next time!";
      break;
    case "SELF_CANCELLED":
      cancelledReason = "You cancelled";
      cancelledExplanation =
        "This session wasn’t counted as a complete session because you cancelled the session before it ended. Try to reach the last step of the session next time!";
      break;
    case "PARTNER_CANCELLED":
      cancelledReason = "Partner cancelled";
      cancelledExplanation =
        "This session wasn’t counted as a complete session because your partner cancelled the session before it ended. ";
      break;
    case "CONNECTION_ISSUES":
      cancelledReason = "Connection issue";
      cancelledExplanation =
        "This session wasn't counted as a complete session because either you or your partner’s internet caused the session to disconnect.";
      break;
  }

  const StyledTableCell = styled(TableCell)(({}) => ({
    border: 0,
  }));

  return (
    <>
      <TableRow key={index}>
        <StyledTableCell>
          <Text>{dateTime}</Text>
        </StyledTableCell>
        <StyledTableCell>
          <Text
            style={{
              color: !session.isCancelled
                ? upduoPrimaryBlack
                : upduoMediumDarkGray,
            }}
          >
            {session.skill.name}
          </Text>
          {session.isCancelled && session.cancelledReason ? (
            <TouchableOpacity
              style={{ marginTop: 2 }}
              onPress={() => {
                setExpanded(!expanded);
              }}
            >
              <Text
                style={{
                  fontSize: 12,
                  color: skalecalendarblue,
                }}
              >
                <Icon size={12} icon={info} />
                <View style={{ width: 3 }} />
                {cancelledReason}
              </Text>
            </TouchableOpacity>
          ) : null}
        </StyledTableCell>
        <StyledTableCell>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <ProfileImage userObject={partner} size={40} />
            <View
              style={{
                marginLeft: 15,
                display: "flex",
                flexDirection: "column",
                flexShrink: 1,
              }}
            >
              <Text numberOfLines={1} ellipsizeMode={"tail"}>
                {`${partnerName}`}
              </Text>
              <Text
                style={{
                  fontSize: 12,
                  color: upduoMediumGray,
                  paddingTop: 5,
                }}
                numberOfLines={1}
                ellipsizeMode={"tail"}
              >
                {partner.title ? partner.title : "-"}
              </Text>
            </View>
          </View>
        </StyledTableCell>
        <StyledTableCell>
          <Text
            style={{
              color: !session.isCancelled
                ? upduoPrimaryBlack
                : upduoMediumDarkGray,
            }}
          >
            {`${minutes}:${seconds}`}
          </Text>
        </StyledTableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingTop: 0, paddingBottom: 0 }} colSpan={4}>
          <Collapse in={expanded} timeout={"auto"} unmountOnExit>
            <View
              style={{
                backgroundColor: upduoBlueBackground,
                paddingHorizontal: 16,
                paddingVertical: 8,
                borderRadius: 8,
                marginBottom: 16,
              }}
            >
              <Text style={{ fontSize: 12 }}>{cancelledExplanation}</Text>
            </View>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
